import React, { useState, useCallback } from "react";
import { X as XIcon, Eye, Pin, PinOff, Ban, Loader2 } from "lucide-react";

// Field options for a condition. "in" fields take comma-separated values.
const IN_FIELDS = [
  { v: "category", label: "Category" },
  { v: "craft", label: "Craft" },
  { v: "occasion", label: "Occasion" },
  { v: "vibe", label: "Vibe" },
  { v: "travel", label: "Travel" },
  { v: "season", label: "Season" },
  { v: "material", label: "Material" },
  { v: "keyword", label: "Keyword (title/desc)" },
];
const ALL_FIELDS = [...IN_FIELDS, { v: "is_ethnic", label: "Is Ethnic" }, { v: "attr", label: "Attribute (bool)" }];

const emptyCond = () => ({ field: "category", in: [] });

const ConditionRow = ({ cond, onChange, onRemove, danger }) => {
  const setField = (field) => {
    if (field === "is_ethnic") onChange({ field, value: true });
    else if (field === "attr") onChange({ field, key: "has_print", value: true });
    else onChange({ field, in: cond.in || [] });
  };
  return (
    <div className={`flex items-start gap-2 mb-2 p-2 border ${danger ? "bg-red-50/50 border-red-100" : "bg-[#FAFAFA] border-[#E5E5E5]"}`}>
      <select value={cond.field} onChange={(e) => setField(e.target.value)} className="border border-[#E5E5E5] px-2 py-1 text-xs flex-shrink-0 bg-white" data-testid="cond-field-select">
        {ALL_FIELDS.map((f) => <option key={f.v} value={f.v}>{f.label}</option>)}
      </select>

      {cond.field === "is_ethnic" && (
        <select value={String(cond.value)} onChange={(e) => onChange({ ...cond, value: e.target.value === "true" })} className="border border-[#E5E5E5] px-2 py-1 text-xs flex-1 bg-white">
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      )}
      {cond.field === "attr" && (
        <div className="flex-1 flex gap-1">
          <input placeholder="key (e.g. has_print)" value={cond.key || ""} onChange={(e) => onChange({ ...cond, key: e.target.value })} className="border border-[#E5E5E5] px-2 py-1 text-xs flex-1" />
          <select value={String(cond.value)} onChange={(e) => onChange({ ...cond, value: e.target.value === "true" })} className="border border-[#E5E5E5] px-2 py-1 text-xs w-20 bg-white">
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        </div>
      )}
      {IN_FIELDS.some((f) => f.v === cond.field) && (
        <div className="flex-1">
          <input placeholder="values (comma-separated)" value={(cond.in || []).join(", ")} onChange={(e) => onChange({ ...cond, in: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="border border-[#E5E5E5] px-2 py-1 text-xs w-full" />
          {cond.field === "keyword" && (
            <label className="flex items-center gap-1 mt-1 text-[10px] text-[#71717A]">
              <input type="checkbox" checked={!!cond.title_only} onChange={(e) => onChange({ ...cond, title_only: e.target.checked })} /> title only
            </label>
          )}
        </div>
      )}
      <button onClick={onRemove} className="text-red-400 hover:text-red-600 flex-shrink-0"><XIcon className="w-3.5 h-3.5" /></button>
    </div>
  );
};

const CollectionBuilder = ({ api, initial, name, onChange }) => {
  const init = initial || {};
  const [department, setDepartment] = useState(init.filter?.department ?? "fashion");
  const [priceMin, setPriceMin] = useState(init.filter?.price_min ?? "");
  const [priceMax, setPriceMax] = useState(init.filter?.price_max ?? "");
  const [matchAny, setMatchAny] = useState(init.filter?.match_any || []);
  const [excludeAny, setExcludeAny] = useState(init.filter?.exclude_any || []);
  const [manualExclude, setManualExclude] = useState(init.manual_exclude || []);
  const [pinnedOrder, setPinnedOrder] = useState(init.pinned_order || []);

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const buildConfig = useCallback((extra = {}) => {
    const filter = {
      department: department || null,
      price_min: priceMin === "" ? null : parseInt(priceMin),
      price_max: priceMax === "" ? null : parseInt(priceMax),
      match_any: matchAny,
      exclude_any: excludeAny,
    };
    return {
      filter,
      manual_exclude: manualExclude,
      pinned_order: pinnedOrder,
      manual_include: pinnedOrder,
      ...extra,
    };
  }, [department, priceMin, priceMax, matchAny, excludeAny, manualExclude, pinnedOrder]);

  // bubble config up whenever it changes
  const emit = useCallback((cfg) => { if (onChange) onChange(cfg); }, [onChange]);

  const update = (setter) => (val) => {
    setter(val);
    // defer emit to next tick using latest buildConfig via microtask
    setTimeout(() => emit(buildConfig()), 0);
  };

  const runPreview = async () => {
    setLoading(true);
    try {
      const cfg = buildConfig();
      const res = await api.post("/admin/collections/preview", { name, ...cfg, sample_limit: 60 });
      setPreview(res.data);
      emit(cfg);
    } catch (e) {
      setPreview({ error: e?.response?.data?.detail || "Preview failed" });
    } finally {
      setLoading(false);
    }
  };

  const togglePin = (id) => update(setPinnedOrder)(pinnedOrder.includes(id) ? pinnedOrder.filter((x) => x !== id) : [...pinnedOrder, id]);
  const toggleExclude = (id) => update(setManualExclude)(manualExclude.includes(id) ? manualExclude.filter((x) => x !== id) : [...manualExclude, id]);

  return (
    <div className="border border-[#0A0A0A]/10 rounded-md p-3 bg-[#FBFBF9]" data-testid="collection-builder">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-[#0A0A0A] uppercase tracking-wider">Collection Query (Option B)</span>
        <button onClick={runPreview} disabled={loading} className="text-[11px] px-3 py-1.5 bg-[#0A0A0A] text-white hover:bg-[#FF4FB4] transition-colors flex items-center gap-1 disabled:opacity-50" data-testid="preview-collection-btn">
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} Preview
        </button>
      </div>

      <div className="flex gap-2 mb-3">
        <div className="flex-1">
          <label className="text-[10px] text-[#71717A] block mb-1">Department</label>
          <select value={department} onChange={(e) => update(setDepartment)(e.target.value)} className="w-full border border-[#E5E5E5] px-2 py-1 text-xs bg-white" data-testid="cb-department">
            {["fashion", "jewellery", "accessories", "bags", "footwear", "cosmetics", "home"].map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="w-24">
          <label className="text-[10px] text-[#71717A] block mb-1">Price min</label>
          <input type="number" value={priceMin} onChange={(e) => update(setPriceMin)(e.target.value)} className="w-full border border-[#E5E5E5] px-2 py-1 text-xs" data-testid="cb-price-min" />
        </div>
        <div className="w-24">
          <label className="text-[10px] text-[#71717A] block mb-1">Price max</label>
          <input type="number" value={priceMax} onChange={(e) => update(setPriceMax)(e.target.value)} className="w-full border border-[#E5E5E5] px-2 py-1 text-xs" data-testid="cb-price-max" />
        </div>
      </div>

      {/* MATCH ANY */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] font-semibold text-[#0A0A0A] uppercase">Include if matches ANY</label>
          <button onClick={() => update(setMatchAny)([...matchAny, emptyCond()])} className="text-[10px] px-2 py-0.5 bg-[#0A0A0A] text-white hover:bg-[#FF4FB4]" data-testid="add-match-btn">+ condition</button>
        </div>
        {matchAny.length === 0 && <p className="text-[10px] text-[#71717A] italic mb-1">No conditions = every {department} product (additive, e.g. Budget Badass).</p>}
        {matchAny.map((c, i) => (
          <ConditionRow key={i} cond={c}
            onChange={(nc) => update(setMatchAny)(matchAny.map((x, idx) => idx === i ? nc : x))}
            onRemove={() => update(setMatchAny)(matchAny.filter((_, idx) => idx !== i))} />
        ))}
      </div>

      {/* EXCLUDE ANY */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] font-semibold text-[#0A0A0A] uppercase">Exclude if matches ANY</label>
          <button onClick={() => update(setExcludeAny)([...excludeAny, emptyCond()])} className="text-[10px] px-2 py-0.5 bg-[#0A0A0A] text-white hover:bg-[#FF4FB4]" data-testid="add-exclude-cond-btn">+ condition</button>
        </div>
        {excludeAny.map((c, i) => (
          <ConditionRow key={i} cond={c} danger
            onChange={(nc) => update(setExcludeAny)(excludeAny.map((x, idx) => idx === i ? nc : x))}
            onRemove={() => update(setExcludeAny)(excludeAny.filter((_, idx) => idx !== i))} />
        ))}
      </div>

      {/* PREVIEW RESULTS */}
      {preview && !preview.error && (
        <div className="mt-3 border-t border-[#E5E5E5] pt-3" data-testid="preview-results">
          <p className="text-xs font-semibold text-[#0A0A0A] mb-2">
            {preview.total_matched} products matched · {preview.unique_brands} brands
            <span className="text-[#71717A] font-normal"> · showing {preview.sample_count} (brand-diverse)</span>
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto">
            {preview.sample.map((p) => {
              const pinned = pinnedOrder.includes(p.id);
              const excluded = manualExclude.includes(p.id);
              return (
                <div key={p.id} className={`relative group border rounded overflow-hidden ${excluded ? "opacity-40 border-red-300" : "border-[#E5E5E5]"}`} data-testid={`preview-item-${p.id}`}>
                  <div className="aspect-[3/4] bg-[#F4F4F4]">
                    {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />}
                  </div>
                  <div className="p-1">
                    <p className="text-[9px] text-[#0A0A0A] truncate font-medium">{p.title}</p>
                    <p className="text-[8px] text-[#71717A] truncate">{p.brand_name} · {p.category}{p.is_ethnic ? " · ethnic" : ""}</p>
                  </div>
                  <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => togglePin(p.id)} title="Pin to top" className={`p-1 rounded ${pinned ? "bg-[#FF4FB4] text-white" : "bg-white/90 text-[#0A0A0A]"}`}>
                      {pinned ? <Pin className="w-3 h-3" /> : <PinOff className="w-3 h-3" />}
                    </button>
                    <button onClick={() => toggleExclude(p.id)} title="Exclude" className={`p-1 rounded ${excluded ? "bg-red-500 text-white" : "bg-white/90 text-red-500"}`}>
                      <Ban className="w-3 h-3" />
                    </button>
                  </div>
                  {pinned && <span className="absolute top-1 left-1 bg-[#FF4FB4] text-white text-[8px] px-1 rounded">PINNED</span>}
                </div>
              );
            })}
          </div>
          {(pinnedOrder.length > 0 || manualExclude.length > 0) && (
            <p className="text-[10px] text-[#71717A] mt-2">{pinnedOrder.length} pinned · {manualExclude.length} excluded (saved with the theme)</p>
          )}
        </div>
      )}
      {preview?.error && <p className="text-xs text-red-500 mt-2">{preview.error}</p>}
    </div>
  );
};

export default CollectionBuilder;
